import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { Badge } from "../badge";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { ACCENT, formatMXN, Section, CustomTooltip } from "../cuentas/shared";
import { getMonthRange, formatDiaEvento, calendarDayAnchor, INSTANTE } from "../../utils/dates";
import { totalCostos } from "../../utils/costos";
import {
  variacion,
  resumenDinero,
  resumenEventos,
  resumenLeads,
  serieMensual,
} from "../../utils/reportes";
import {
  Kpi,
  fmtPct,
  ultimosMeses,
  labelMes,
  COSTO_COLOR,
  VALOR_COLOR,
  useDepositos,
} from "./shared";
import {
  CurrencyDollarIcon,
  ClipboardDocumentCheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/20/solid";

// Marca de estado junto al nombre del evento; la leyenda de la tabla dice
// qué significa cada color, así el renglón no repite el texto.
const MARCAS = {
  futuro: { clase: "bg-blue-500", label: "Por realizar" },
  cancelado: { clase: "bg-red-500", label: "Cancelado" },
};
const MarcaEstado = ({ tipo }) => (
  <span
    className={`inline-block h-2.5 w-2.5 shrink-0 rounded-sm ${MARCAS[tipo].clase}`}
    title={MARCAS[tipo].label}
    aria-label={MARCAS[tipo].label}
    role="img"
  />
);

// Encabezado ordenable de la tabla de eventos: mismo patrón que Users.js.
// Fuera del componente para que el botón no se vuelva a montar (y pierda
// el foco) en cada clic.
const ThOrden = ({ campo, orden, onOrdenar, derecha, children }) => {
  const activo = orden.campo === campo;
  const Icono = !activo ? ChevronUpDownIcon : orden.dir === "asc" ? ChevronUpIcon : ChevronDownIcon;
  return (
    <th
      className={`px-3 py-2 font-semibold text-zinc-600 whitespace-nowrap ${derecha ? "text-right" : "text-left"}`}
      aria-sort={activo ? (orden.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onOrdenar(campo)}
        className={`inline-flex items-center gap-1 hover:text-zinc-900 dark:hover:text-white ${derecha ? "flex-row-reverse" : ""}`}
      >
        {children}
        <Icono className={`h-4 w-4 ${activo ? "" : "text-zinc-300"}`} />
      </button>
    </th>
  );
};

// El vistazo general: los KPIs del período, la tendencia de 12 meses y los
// mejores eventos. Para profundizar en cada tema están los demás reportes.
const ReporteResumen = ({ rango, depositos: depositosProp = null }) => {
  const navigate = useNavigate();
  const {
    events,
    payments,
    refunds,
    costos,
    contactForms,
    calendarTimezone: tz,
  } = useVenue();

  // Los depósitos entran solo para saber cuánto se aplicó en el período:
  // el ingreso que no viene de ningún evento. ReportesPage ya los cargó.
  const depositosCargados = useDepositos(!depositosProp);
  const depositos = depositosProp || depositosCargados;
  const dinero = useMemo(
    () => resumenDinero({ payments, refunds, costos, depositos, desde: rango.from, hasta: rango.to, timeZone: tz }),
    [payments, refunds, costos, depositos, rango, tz]
  );
  const dineroPrev = useMemo(
    () => resumenDinero({ payments, refunds, costos, depositos, desde: rango.prev.from, hasta: rango.prev.to, timeZone: tz }),
    [payments, refunds, costos, depositos, rango, tz]
  );
  const evts = useMemo(
    () => resumenEventos({ events, costos, refunds, desde: rango.from, hasta: rango.to, timeZone: tz }),
    [events, costos, refunds, rango, tz]
  );
  const evtsPrev = useMemo(
    () => resumenEventos({ events, costos, desde: rango.prev.from, hasta: rango.prev.to, timeZone: tz }),
    [events, costos, rango, tz]
  );
  const leads = useMemo(
    () => resumenLeads({ contactForms, desde: rango.from, hasta: rango.to, timeZone: tz }),
    [contactForms, rango, tz]
  );
  const leadsPrev = useMemo(
    () => resumenLeads({ contactForms, desde: rango.prev.from, hasta: rango.prev.to, timeZone: tz }),
    [contactForms, rango, tz]
  );

  const serie = useMemo(
    () =>
      serieMensual({
        meses: ultimosMeses(12),
        events,
        payments,
        refunds,
        costos,
        contactForms,
        timeZone: tz,
        getMonthRange,
      }).map((m) => ({ ...m, label: labelMes(m.key) })),
    [events, payments, refunds, costos, contactForms, tz]
  );
  const hayMovimiento = serie.some((m) => m.cobrado > 0 || m.costos > 0 || m.eventos > 0);

  const porPagarHoy = useMemo(
    () => totalCostos(costos, { estado: "por_pagar" }),
    [costos]
  );

  // La tabla junta los eventos activos con los cancelados del período: de
  // un cancelado importa lo que el venue retuvo. Ordenable por columna; por
  // omisión, mejor utilidad primero. Los vacíos (valor y utilidad de un
  // cancelado) van siempre al final, en cualquier dirección.
  const [orden, setOrden] = useState({ campo: "utilidad", dir: "desc" });
  const ordenarPor = (campo) =>
    setOrden((o) =>
      o.campo === campo
        ? { campo, dir: o.dir === "asc" ? "desc" : "asc" }
        : { campo, dir: campo === "nombre" || campo === "fecha" ? "asc" : "desc" }
    );
  const filas = useMemo(() => {
    const valorDe = (e) => {
      switch (orden.campo) {
        case "nombre":
          return e.nombre.toLocaleLowerCase("es-MX");
        case "fecha":
          return calendarDayAnchor(e.startTime, tz, INSTANTE)?.getTime() ?? null;
        case "pagado":
          return e.cancelado ? e.retenido : e.pagado;
        case "valor":
        case "utilidad":
          return e.cancelado ? null : e[orden.campo];
        default:
          return e[orden.campo];
      }
    };
    const signo = orden.dir === "asc" ? 1 : -1;
    return [...evts.porEvento, ...evts.eventosCancelados].sort((a, b) => {
      const va = valorDe(a);
      const vb = valorDe(b);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      const cmp = typeof va === "string" ? va.localeCompare(vb, "es-MX") : va - vb;
      return cmp * signo;
    });
  }, [evts, orden, tz]);
  // "Ver todos" expande la lista completa aquí mismo; el corte a 5 es solo
  // el resumen, no un tope.
  const [verTodos, setVerTodos] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const eventosVisibles = verTodos ? filas : filas.slice(0, 5);
  const abrirRentabilidad = () =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("r", "rentabilidad");
      return next;
    }, { replace: true });
  const peorEvento = evts.porEvento[evts.porEvento.length - 1];

  return (
    <div className="space-y-6">
      {/* KPIs de dinero */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Valor de eventos"
          value={formatMXN(evts.valor)}
          delta={variacion(evts.valor, evtsPrev.valor)}
          hint={
            evts.porRealizar > 0
              ? `${evts.eventos} ${evts.eventos === 1 ? "evento" : "eventos"} · ${formatMXN(evts.valorPorRealizar)} por realizar`
              : `${evts.eventos} ${evts.eventos === 1 ? "evento" : "eventos"} en el período`
          }
        />
        <Kpi
          label="Utilidad de eventos"
          value={formatMXN(evts.utilidad)}
          delta={variacion(evts.utilidad, evtsPrev.utilidad)}
          tone={evts.utilidad < 0 ? "text-red-600" : "text-emerald-600"}
          hint={
            evts.margen !== null
              ? evts.porRealizar > 0
                ? `Margen ${(evts.margen * 100).toFixed(1)}% · ${formatMXN(evts.utilidadPorRealizar)} por realizar`
                : `Margen ${(evts.margen * 100).toFixed(1)}% · valor menos costos de esos eventos`
              : "Sin valor capturado en el período"
          }
        />
        <Kpi
          label="Ingresos"
          value={formatMXN(dinero.cobrado)}
          delta={variacion(dinero.cobrado, dineroPrev.cobrado)}
          hint={
            dinero.depositosAplicados > 0
              ? `${dinero.pagosCount} ${dinero.pagosCount === 1 ? "pago" : "pagos"} recibidos · ${formatMXN(dinero.depositosAplicados)} en depósitos aplicados`
              : `${dinero.pagosCount} ${dinero.pagosCount === 1 ? "pago" : "pagos"} recibidos`
          }
        />
        <Kpi
          label="Neto de operación"
          value={formatMXN(dinero.neto)}
          delta={variacion(dinero.neto, dineroPrev.neto)}
          tone={dinero.neto < 0 ? "text-red-600" : "text-emerald-600"}
          hint="Ingresos − reembolsos − egresos pagados. Sin depósitos ni retiros: esos están en Flujo de caja"
        />
      </div>

      {/* KPIs de operación */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Eventos del período"
          value={evts.eventos}
          delta={variacion(evts.eventos, evtsPrev.eventos)}
          hint={
            evts.porRealizar > 0
              ? `${evts.realizados} ${evts.realizados === 1 ? "realizado" : "realizados"} · ${evts.porRealizar} por realizar`
              : evts.eventos > 0
                ? `Valor promedio ${formatMXN(evts.valorPromedio)}`
                : null
          }
        />
        <Kpi
          label="Leads recibidos"
          value={leads.recibidos}
          delta={variacion(leads.recibidos, leadsPrev.recibidos)}
          hint={leads.ganados > 0 ? `${leads.ganados} ganados` : null}
        />
        <Kpi
          label="Tasa de cierre"
          value={fmtPct(leads.tasaGanados)}
          delta={variacion(leads.tasaGanados, leadsPrev.tasaGanados)}
          hint="Ganados entre los leads que llegaron en el período"
        />
        <Kpi
          label="Por pagar hoy"
          value={formatMXN(porPagarHoy)}
          tone={porPagarHoy > 0 ? "text-amber-600" : ""}
          // Foto del día: es el único cuadro que no se mueve con el filtro,
          // y hay que decirlo para que no parezca que el filtro falla.
          hint={
            porPagarHoy > 0 ? (
              <>
                Al día de hoy, sin importar el período ·{" "}
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/cuentas/por-pagar")}
                  className="hover:underline"
                  style={{ color: ACCENT }}
                >
                  Ver a quién le debes →
                </button>
              </>
            ) : (
              "No debes nada registrado · al día de hoy, sin importar el período"
            )
          }
        />
      </div>

      {/* Tendencias 12 meses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section icon={CurrencyDollarIcon} title="Caja por mes">
          {hayMovimiento ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={serie} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    width={48}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="cobrado" name="Ingresos" fill={ACCENT} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="costos" name="Egresos" fill={COSTO_COLOR} radius={[4, 4, 0, 0]} />
                  <Line
                    type="monotone"
                    dataKey="neto"
                    name="Neto"
                    stroke="#52525b"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-zinc-400 py-10 text-center">
              Sin movimientos todavía. Captura ingresos y egresos para ver la
              tendencia.
            </p>
          )}
        </Section>

        <Section icon={ClipboardDocumentCheckIcon} title="Valor y utilidad por mes">
          {hayMovimiento ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={serie} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    width={48}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="valor" name="Valor de eventos" fill={VALOR_COLOR} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="utilidad" name="Utilidad" fill={ACCENT} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-zinc-400 py-10 text-center">
              Aquí se compara lo que valen tus eventos contra lo que te dejan,
              mes a mes.
            </p>
          )}
        </Section>
      </div>

      {/* Eventos del período, activos y cancelados */}
      <Section
        icon={ClipboardDocumentCheckIcon}
        title={`Eventos del período (${evts.eventos})`}
        action={
          evts.cancelados > 0 ? (
            <Badge color="red">{evts.cancelados} cancelados</Badge>
          ) : null
        }
      >
        {filas.length === 0 ? (
          <p className="text-sm text-zinc-400">No hay eventos en este período.</p>
        ) : (
          <>
            {(evts.porRealizar > 0 || evts.cancelados > 0) && (
              <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
                {evts.porRealizar > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <MarcaEstado tipo="futuro" /> Por realizar
                  </span>
                )}
                {evts.cancelados > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <MarcaEstado tipo="cancelado" /> Cancelado · se muestra lo retenido
                  </span>
                )}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-100 dark:border-zinc-700/40">
                    <ThOrden orden={orden} onOrdenar={ordenarPor} campo="nombre">Evento</ThOrden>
                    <ThOrden orden={orden} onOrdenar={ordenarPor} campo="fecha">Fecha</ThOrden>
                    <ThOrden orden={orden} onOrdenar={ordenarPor} campo="valor" derecha>Valor</ThOrden>
                    <ThOrden orden={orden} onOrdenar={ordenarPor} campo="pagado" derecha>Pagado</ThOrden>
                    <ThOrden orden={orden} onOrdenar={ordenarPor} campo="costos" derecha>Costos</ThOrden>
                    <ThOrden orden={orden} onOrdenar={ordenarPor} campo="utilidad" derecha>Utilidad</ThOrden>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {eventosVisibles.map((e) => (
                    <tr key={e.id}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {(e.futuro || e.cancelado) && (
                            <MarcaEstado tipo={e.cancelado ? "cancelado" : "futuro"} />
                          )}
                          <button
                            type="button"
                            onClick={() => navigate(`/dashboard/eventos/${e.id}`)}
                            className="hover:underline text-left"
                            style={{ color: ACCENT }}
                          >
                            {e.nombre}
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">
                        {formatDiaEvento(e.startTime, tz, INSTANTE) || "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-zinc-600">
                        {e.cancelado ? "—" : formatMXN(e.valor)}
                      </td>
                      {/* En un cancelado, lo que se muestra es lo que el venue
                          retuvo: lo pagado menos lo reembolsado. */}
                      <td className="px-3 py-2 text-right text-zinc-600 whitespace-nowrap">
                        {formatMXN(e.cancelado ? e.retenido : e.pagado)}
                        {e.cancelado ? (
                          e.retenido > 0.01 ? (
                            <div className="text-xs text-zinc-500">
                              Retenido{e.reembolsado > 0.01 ? ` · reembolsado ${formatMXN(e.reembolsado)}` : ""}
                            </div>
                          ) : e.reembolsado > 0.01 ? (
                            <div className="text-xs text-zinc-500">Reembolsado completo</div>
                          ) : null
                        ) : (
                          e.saldo > 0.01 && (
                            <div className="text-xs text-amber-600">Faltan {formatMXN(e.saldo)}</div>
                          )
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-red-600">
                        {e.costos > 0 ? formatMXN(e.costos) : "—"}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-medium ${
                          e.cancelado ? "text-zinc-400" : e.utilidad < 0 ? "text-red-600" : "text-emerald-600"
                        }`}
                      >
                        {e.cancelado ? "—" : formatMXN(e.utilidad)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filas.length > 5 && (
              <p className="mt-2 text-xs text-zinc-400">
                {verTodos
                  ? `Los ${filas.length} eventos del período. `
                  : `Mostrando 5 de ${filas.length}. `}
                <button
                  type="button"
                  onClick={() => setVerTodos((v) => !v)}
                  className="font-semibold text-blue-600 hover:underline"
                >
                  {verTodos ? "Ver solo 5" : `Ver los ${filas.length}`}
                </button>
                {" · "}
                <button
                  type="button"
                  onClick={abrirRentabilidad}
                  className="font-semibold text-blue-600 hover:underline"
                >
                  Abrir Rentabilidad
                </button>
              </p>
            )}
            {peorEvento && peorEvento.utilidad < 0 && (
              <p className="mt-2 text-xs text-red-600">
                Atención:{" "}
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/eventos/${peorEvento.id}`)}
                  className="font-medium hover:underline"
                >
                  {peorEvento.nombre}
                </button>{" "}
                cerró en negativo ({formatMXN(peorEvento.utilidad)}).
              </p>
            )}
          </>
        )}
      </Section>
    </div>
  );
};

export default ReporteResumen;
